import { MeContext } from '../../../context/MeContext';
import type { MeContextState } from '../../../store/types/account';
import {
  AspectRatio,
  Box,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Image,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useContext,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  APPLE_PHONE_BASE64,
  GOOGLE_PHONE_BASE64,
  GOOGLE_PIXEL9_PHONE_BASE64,
  GOOGLE_DEFAULT_WAVE_BANNER_BASE64,
} from '../../../utils/base64';
import ApplePassPreview from './previews/apple/ApplePassPreview';
import ApplePosterPreview from './previews/apple/ApplePosterPreview';
import ApplePosterGenericPreview from './previews/apple/ApplePosterGenericPreview';
import ApplePosterDemo from './previews/apple/demoPass/ApplePosterDemo';
import { useDemoPass } from './DemoPassContext';
import GooglePassPreview from './previews/google/GooglePassPreview';
import GoogleExpressivePassPreview from './previews/google/GoogleExpressivePassPreview';
import GoogleScrollButton from './previews/google/GoogleScrollButton';
import BarcodePreview from './previews/BarcodePreview';
import { getGoogleBackgroundColor } from './utils/googlePassPreview';
import { getContrastYIQ } from '../../../utils/colorBasedOnBgContrast';
import { usePreviewEnhancedDesign } from './hooks/usePreviewEnhancedDesign';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import PassContext from '../../../store/client/PassContext';
import {
  QrFeature,
  PassDesign,
  FieldUIState,
  type PreviewData,
} from '../../../store/types/template';
import JsonView from '@uiw/react-json-view';
import { CopyIcon } from '@chakra-ui/icons';
import { CustomInfoIcon } from '../../../theme/icons/CustomInfoIcon';
import CustomToast from '../../../common/CustomToast';
import { formatTemplatePayload } from './utils/formatTemplatePayload';
import { getEffectiveTemplateImage } from './utils/imageHelpers';
import {
  STAMP_WORDS,
  STAMP_NUMBER_WORDS,
  getStampImageKey,
  getStampShortValueKey,
  getStampLongValueKey,
  STAMP_INDEX,
} from './utils/stampCardConstants';
import { setFocusedField } from '../../../store/redux/slices/template.slice';
import { CustomChevronLeftIcon } from '../../../theme/icons/CustomChevronLeftIcon';
import {
  BUILDER_DEVICE_TAB,
  type BuilderDeviceTab,
  type BuilderPassTab,
} from './utils/templateBuilderTabs';
import {
  PREVIEW_DEVICE_WIDTH,
  RIGHT_OF_PREV_RAIL_CENTRE,
  LEFT_OF_PREV_RAIL_CENTRE,
} from './utils/previewLayout';
import PreviewActionsPill from './PreviewActionsPill';
import { GoogleArrowBackIcon } from '../../../theme/icons/googlePreviewIcons';
import GooglePreviewWizard, {
  markStepSeen,
} from './previews/google/GooglePreviewWizard';

type PassContextValue = {
  headerOne?: string | number | { value?: string | number };
  headerTwo?: string | number | { value?: string | number };
  headerThree?: string | number | { value?: string | number };
  primaryOne?: string | number | { value?: string | number };
  primaryTwo?: string | number | { value?: string | number };
  detailsOne?: string | number | { value?: string | number };
  detailsTwo?: string | number | { value?: string | number };
  detailsThree?: string | number | { value?: string | number };
  passDesign?: unknown;
  barcode?: unknown;
  nfc?: { enabled?: boolean } | null;
  formEnabled?: boolean;
  googleAppLinkIconUrl?: string;
};

type PreviewTab =
  | 'Apple'
  | 'Google'
  | 'Enhanced'
  | 'Demo pass'
  | 'iOS 27+'
  | 'iOS 26';

const PreviewPanel = ({
  selectedPassTab,
  setSelectedPassTab,
  selectedTab,
  externalPreviewData,
  isPassPreview = false,
  devModeData,
  passLanguages,
  passDefaultLanguage,
  selectedPassLanguage,
  onPassLanguageChange,
  onEnhancedTabActiveChange,
  googleRevertedPreview: googleRevertedPreviewProp,
  setGoogleRevertedPreview: setGoogleRevertedPreviewProp,
  onGooglePreviewActiveChange,
}: {
  selectedPassTab?: BuilderPassTab;
  setSelectedPassTab?: Dispatch<SetStateAction<BuilderPassTab>>;
  selectedTab?: BuilderDeviceTab;
  externalPreviewData?: PreviewData;
  isPassPreview?: boolean;
  devModeData?: Record<string, unknown>;
  passLanguages?: string[];
  passDefaultLanguage?: string;
  selectedPassLanguage?: string | null;
  onPassLanguageChange?: (lang: string | null) => void;
  onEnhancedTabActiveChange?: (isActive: boolean) => void;
  googleRevertedPreview?: boolean;
  setGoogleRevertedPreview?: Dispatch<SetStateAction<boolean>>;
  onGooglePreviewActiveChange?: (isActive: boolean) => void;
}) => {
  const { state: meState } = useContext(MeContext) as { state: MeContextState };
  const expressivePassRolloutEnabled =
    meState?.expressivePassRolloutEnabled === true;
  const expressivePassRevertButtonEnabled =
    meState?.expressivePassRevertButtonEnabled === true;
  const [localGoogleRevertedPreview, setLocalGoogleRevertedPreview] =
    useState(false);
  const googleRevertedPreview =
    googleRevertedPreviewProp ?? localGoogleRevertedPreview;
  const setGoogleRevertedPreview =
    setGoogleRevertedPreviewProp ?? setLocalGoogleRevertedPreview;
  const [googleBarcodeView, setGoogleBarcodeView] = useState(false);

  const googleScrollRef = useRef<HTMLDivElement>(null);
  const [googleIsAtBottom, setGoogleIsAtBottom] = useState(false);
  const [googleBounceCount, setGoogleBounceCount] = useState(0);

  const handleGoogleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 5;
      setGoogleIsAtBottom(atBottom);
    },
    []
  );

  const scrollButtonRef = useRef<HTMLDivElement>(null);
  const barcodeButtonRef = useRef<HTMLElement>(null);
  const revertButtonRef = useRef<HTMLElement>(null);

  const previewEnhancedDesign = usePreviewEnhancedDesign();
  const hasEnhancedArtwork = useAppSelector(
    (s) => !!s.template.appleEnhancedDesign.images.artwork
  );
  const isAppleDeviceTab = selectedTab === BUILDER_DEVICE_TAB.APPLE;
  const isSharedDeviceTab = selectedTab === BUILDER_DEVICE_TAB.SHARED;
  const enhancedFaceAvailable =
    previewEnhancedDesign !== null && hasEnhancedArtwork;
  const defaultTab: PreviewTab = !isAppleDeviceTab
    ? 'Apple'
    : previewEnhancedDesign === 'generic'
      ? enhancedFaceAvailable
        ? 'iOS 27+'
        : 'iOS 26'
      : previewEnhancedDesign === 'eventTicket' && enhancedFaceAvailable
        ? 'Enhanced'
        : 'Apple';
  const [active, setActive] = useState<PreviewTab>(defaultTab);
  const [appleRevertedPreview, setAppleRevertedPreview] = useState(false);
  const buttons = useMemo<PreviewTab[]>(() => {
    if (!isAppleDeviceTab) return ['Apple', 'Google'];
    if (previewEnhancedDesign === 'eventTicket') {
      return ['Apple', 'Enhanced', 'Demo pass'];
    }
    if (previewEnhancedDesign === 'generic') {
      return ['iOS 27+', 'iOS 26'];
    }
    return ['Apple', 'Google'];
  }, [isAppleDeviceTab, previewEnhancedDesign]);
  useEffect(() => {
    if (!buttons.includes(active)) {
      setActive(buttons[0]);
    }
  }, [buttons, active]);
  const showAppleRevert =
    isSharedDeviceTab && active === 'Apple' && enhancedFaceAvailable;
  const showAppleEnhancedFace = showAppleRevert && !appleRevertedPreview;
  useEffect(() => {
    if (!showAppleRevert) setAppleRevertedPreview(false);
  }, [showAppleRevert]);
  const revert =
    active === 'Apple'
      ? {
          toggle: () => setAppleRevertedPreview((prev) => !prev),
          isReverted: appleRevertedPreview,
          label: 'Older devices' as string | undefined,
          seenStep: 'olderDevices' as const,
        }
      : {
          toggle: () => {
            setGoogleRevertedPreview((prev) => !prev);
            setGoogleBarcodeView(false);
          },
          isReverted: googleRevertedPreview,
          label: undefined as string | undefined,
          seenStep: 'revert' as const,
        };
  // Bounce the scroll button when switching to Google
  // 3 bounces on first visit within this template session, 1 bounce on revisits
  const googleFirstVisitRef = useRef(true);
  useEffect(() => {
    if (active !== 'Google') {
      setGoogleBarcodeView(false);
      setGoogleRevertedPreview(false);
    }
    if (active === 'Google' && expressivePassRolloutEnabled) {
      setGoogleIsAtBottom(false);
      if (googleFirstVisitRef.current) {
        googleFirstVisitRef.current = false;
        setGoogleBounceCount(3);
      } else {
        setGoogleBounceCount((prev) => prev + 1);
      }
    }
  }, [active, expressivePassRolloutEnabled]);

  const { setIsDemoPass } = useDemoPass();
  useEffect(() => {
    setIsDemoPass(active === 'Demo pass');
    return () => setIsDemoPass(false);
  }, [active, setIsDemoPass]);
  useEffect(() => {
    onEnhancedTabActiveChange?.(active === 'Enhanced');
    return () => onEnhancedTabActiveChange?.(false);
  }, [active, onEnhancedTabActiveChange]);
  useEffect(() => {
    onGooglePreviewActiveChange?.(active === 'Google');
    return () => onGooglePreviewActiveChange?.(false);
  }, [active, onGooglePreviewActiveChange]);
  const template = useAppSelector((s) => s.template);
  // Narrow references for useMemo deps — these are stable sub-objects that
  // only change when their section is edited, avoiding a full recompute when
  // unrelated template fields (like _isCreating) change.
  const tFields = template.fields;
  const tImages = template.images;
  const tColors = template.colors;
  const tAppLinking = template.appLinking;
  const tBarcode = template.barcode;
  const tNfc = template.nfc;
  const isTransit =
    (externalPreviewData?.passType ?? template.templateType) === 'transit';
  const showGoogleExpressivePass =
    expressivePassRolloutEnabled && !googleRevertedPreview && !isTransit;

  // Reset revert/barcode state when transit hides Google expressive pass
  useEffect(() => {
    if (isTransit) {
      setGoogleRevertedPreview(false);
      setGoogleBarcodeView(false);
    }
  }, [isTransit]);

  // Exit barcode view when its prerequisites are disabled (template builder only)
  useEffect(() => {
    const nfcActive =
      template.templateType === 'loyalty' ||
      template.templateType === 'stampCard'
        ? template.nfc?.googleNfc
        : template.nfc?.enabled;
    if (
      !externalPreviewData &&
      googleBarcodeView &&
      (!nfcActive || !template.barcode?.enabled)
    ) {
      setGoogleBarcodeView(false);
    }
  }, [
    googleBarcodeView,
    externalPreviewData,
    template.nfc?.googleNfc,
    template.nfc?.enabled,
    template.barcode?.enabled,
    template.templateType,
  ]);
  const dispatch = useAppDispatch();
  const passCtx = useContext(PassContext) as PassContextValue | undefined;
  const passContent = passCtx;
  const focusedField = template.focusedField;
  const previewLanguage = template.previewLanguage;
  const defaultLanguage = template.defaultLanguage || 'en';
  const [featuresSide, setFeaturesSide] = useState<'Front' | 'Back'>('Front');
  const [previewFlipped, setPreviewFlipped] = useState(false);
  const [isDevMode, setIsDevMode] = useState(false);
  const [activeStampIndex, setActiveStampIndex] = useState(0);
  const toast = useToast();

  const handleFlipPreview = () => {
    if (selectedPassTab === 'Features') {
      setFeaturesSide((side) => (side === 'Front' ? 'Back' : 'Front'));
    } else {
      setPreviewFlipped((prev) => !prev);
    }
  };

  const isStampCard = template.templateType === 'stampCard';
  const stampNavOffset = '120px';
  const stampNavOnRail = true;
  const stampCount = useMemo(
    () =>
      isStampCard
        ? Math.min(Math.max(Number(template.stampCount) || 2, 2), 12)
        : 0,
    [isStampCard, template.stampCount]
  );
  const maxStampIndex = stampCount;

  const isFrontOrBackFieldFocused = useMemo(
    () =>
      focusedField != null &&
      (focusedField.startsWith('header.') ||
        focusedField.startsWith('central.') ||
        focusedField.startsWith('details.') ||
        focusedField.startsWith('back.')),
    [focusedField]
  );

  useEffect(() => {
    if (isStampCard && activeStampIndex > maxStampIndex) {
      setActiveStampIndex(maxStampIndex);
    }
  }, [isStampCard, activeStampIndex, maxStampIndex]);

  // When editing a notification message, stamp banner, or stamp short/long value, switch stamp preview to that stage
  useEffect(() => {
    if (!isStampCard || !focusedField) return;
    const notifMatch = focusedField.match(
      /^notificationMessages\.(\d+)\.label$/
    );
    if (notifMatch) {
      const index = parseInt(notifMatch[1], 10);
      if (index >= 0 && index <= maxStampIndex) {
        setActiveStampIndex(index);
      }
      return;
    }
    const stampValueRegex = new RegExp(
      `^stamp(Short|Long)Values\\.stamp(${STAMP_NUMBER_WORDS.join('|')})(Short|Long)Value$`
    );
    const stampValueMatch = focusedField.match(stampValueRegex);
    if (stampValueMatch) {
      const word = stampValueMatch[2];
      const index = STAMP_NUMBER_WORDS.indexOf(
        word as (typeof STAMP_NUMBER_WORDS)[number]
      );
      if (index >= 0 && index <= maxStampIndex) {
        setActiveStampIndex(index);
      }
      return;
    }
    const bannerMatch = focusedField.match(
      /^design\.(shared|apple|google)\.(stamp\w+Image)$/
    );
    if (bannerMatch) {
      const index = STAMP_INDEX[bannerMatch[2]];
      if (index !== undefined && index >= 0 && index <= maxStampIndex) {
        setActiveStampIndex(index);
      }
    }
  }, [isStampCard, focusedField, maxStampIndex]);
  const jsonContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setPreviewFlipped(false);
    dispatch(setFocusedField(null));
  }, [selectedPassTab, selectedTab, dispatch]);

  // Auto-scroll Google expressive pass preview when switching tabs
  useEffect(() => {
    if (!showGoogleExpressivePass || active !== 'Google') return;
    const container = googleScrollRef.current;
    if (!container) return;

    if (selectedPassTab === 'Front') {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (selectedPassTab !== 'Back') return;
    const raf = requestAnimationFrame(() => {
      const fieldsSection = container.querySelector(
        '[data-section="additional-fields"]'
      ) as HTMLElement | null;
      if (fieldsSection) {
        const containerRect = container.getBoundingClientRect();
        const sectionRect = fieldsSection.getBoundingClientRect();
        const scrollOffset =
          sectionRect.top - containerRect.top + container.scrollTop - 20;
        container.scrollTo({ top: scrollOffset, behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedPassTab, active, showGoogleExpressivePass]);

  const effectivePreviewSide = useMemo(() => {
    let side: 'Front' | 'Back';
    const isBackFieldFocused = focusedField?.startsWith('back.');
    const isDescriptionFocused = focusedField === 'description';
    if (
      (active === 'Apple' ||
        active === 'Enhanced' ||
        active === 'iOS 26' ||
        active === 'iOS 27+') &&
      (isBackFieldFocused || isDescriptionFocused) &&
      (selectedPassTab === 'Front' ||
        (selectedPassTab === 'Features' && featuresSide === 'Front'))
    ) {
      side = 'Back';
    } else if (selectedPassTab === 'Features') {
      side = featuresSide;
    } else {
      side = (selectedPassTab as 'Front' | 'Back') ?? 'Front';
    }
    if (previewFlipped && selectedPassTab !== 'Features') {
      return side === 'Front' ? 'Back' : 'Front';
    }
    return side;
  }, [focusedField, active, selectedPassTab, featuresSide, previewFlipped]);

  const effectiveGooglePreviewSide = useMemo(() => {
    let side: 'Front' | 'Back';
    const isBackFieldFocused = focusedField?.startsWith('back.');
    if (
      active === 'Google' &&
      isBackFieldFocused &&
      (selectedPassTab === 'Front' ||
        (selectedPassTab === 'Features' && featuresSide === 'Front'))
    ) {
      side = 'Back';
    } else if (selectedPassTab === 'Features') {
      side = featuresSide;
    } else {
      side = (selectedPassTab as 'Front' | 'Back') ?? 'Front';
    }
    if (previewFlipped && selectedPassTab !== 'Features') {
      return side === 'Front' ? 'Back' : 'Front';
    }
    return side;
  }, [focusedField, active, selectedPassTab, featuresSide, previewFlipped]);

  const addedLanguages = useMemo(() => {
    const languageSet = new Set<string>();

    Object.values(template.fields).forEach((sectionFields) => {
      Object.values(sectionFields).forEach((field) => {
        if (field?.localisation) {
          Object.keys(field.localisation).forEach((langCode) => {
            languageSet.add(langCode);
          });
        }
      });
    });

    return Array.from(languageSet).filter((lang) => lang !== defaultLanguage);
  }, [template.fields, defaultLanguage]);

  const extractFieldValue = (field: any) => {
    if (!field) return '';
    if (typeof field === 'object' && field.value !== undefined) {
      return field.value;
    }
    return field;
  };

  const prepareBackFieldValue = (
    field: FieldUIState | undefined,
    value: string
  ): string => {
    return value;
  };

  const getLocalizedValue = (
    fieldData: FieldUIState | undefined,
    defaultValue: string,
    passContentValue?: any
  ) => {
    if (passContentValue !== undefined && passContentValue !== null) {
      const extracted = extractFieldValue(passContentValue);
      if (extracted) return extracted.toString();
    }
    if (previewLanguage && fieldData?.localisation?.[previewLanguage]) {
      const localized = fieldData.localisation[previewLanguage].defaultValue;
      if (localized) return localized;
    }
    return defaultValue || '';
  };

  const getLocalizedLabel = (fieldData: FieldUIState | undefined) => {
    if (previewLanguage && fieldData?.localisation?.[previewLanguage]) {
      const localized = fieldData.localisation[previewLanguage].label;
      if (localized) return localized;
    }
    return fieldData?.label || '';
  };

  const isFieldHighlighted = (fieldIdentifier: string) => {
    return focusedField === fieldIdentifier;
  };

  const previewData = useMemo(() => {
    const locationMessageFocus = focusedField?.match(
      /^locations\.(\d+)\.message$/
    );
    const appleLocationNotificationMessage =
      selectedTab === BUILDER_DEVICE_TAB.SHARED && locationMessageFocus
        ? (() => {
            const idx = parseInt(locationMessageFocus[1], 10);
            const loc = template.locations[idx];
            if (!loc) return undefined;
            const text = loc.message ?? '';
            return text.trim() === '' ? undefined : text;
          })()
        : undefined;

    const base = {
      templateName: template.templateName,
      passType: template.templateType,
      transitType: template.transitType,
      description: template.description,
      centralTitle: template.centralTitle,
      headerOneId: template.fields.header.one?.id,
      headerOneLabel: getLocalizedLabel(template.fields.header.one),
      headerOneValue: getLocalizedValue(
        template.fields.header.one,
        template.fields.header.one?.defaultValue || '',
        passContent.headerOne
      ),
      headerTwoId: template.fields.header.two?.id,
      headerTwoLabel: getLocalizedLabel(template.fields.header.two),
      headerTwoValue: getLocalizedValue(
        template.fields.header.two,
        template.fields.header.two?.defaultValue || '',
        passContent.headerTwo
      ),
      headerThreeId: template.fields.header.three?.id,
      headerThreeLabel: getLocalizedLabel(template.fields.header.three),
      headerThreeValue: getLocalizedValue(
        template.fields.header.three,
        template.fields.header.three?.defaultValue || '',
        passContent.headerThree
      ),
      primaryOneId: template.fields.central.one?.id,
      primaryOneLabel: getLocalizedLabel(template.fields.central.one),
      primaryOneValue: getLocalizedValue(
        template.fields.central.one,
        template.fields.central.one?.defaultValue || '',
        passContent.primaryOne
      ),
      primaryTwoId: template.fields.central.two?.id,
      primaryTwoLabel: getLocalizedLabel(template.fields.central.two),
      primaryTwoValue: getLocalizedValue(
        template.fields.central.two,
        template.fields.central.two?.defaultValue || '',
        passContent.primaryTwo
      ),
      logoImage: template.images.shared.logo,
      appleLogoImage: template.images.apple.logo,
      googleLogoImage: template.images.google.logo,
      googleWideLogoImage:
        template.chosenGoogleLogo === 'wide_logo'
          ? (template.images.google.wideLogo || template.images.shared.logo || '')
          : '',
      iconLogo: template.images.shared.icon,
      appleIconLogo: template.images.apple.icon,
      googleIconLogo: template.images.google.icon,
      bannerImage: template.images.shared.banner,
      appleBannerImage: template.images.apple.banner,
      googleBannerImage:
        template.images.google.banner ||
        (template.templateType === 'generic'
          ? GOOGLE_DEFAULT_WAVE_BANNER_BASE64
          : ''),
      thumbnailImage: template.images.apple.thumbnail,
      // monochromeBadge: template.design.google.,
      detailsOneId: template.fields.details.one?.id,
      detailsOneLabel: getLocalizedLabel(template.fields.details.one),
      detailsOneValue: getLocalizedValue(
        template.fields.details.one,
        template.fields.details.one?.defaultValue || '',
        passContent.detailsOne
      ),
      detailsTwoId: template.fields.details.two?.id,
      detailsTwoLabel: getLocalizedLabel(template.fields.details.two),
      detailsTwoValue: getLocalizedValue(
        template.fields.details.two,
        template.fields.details.two?.defaultValue || '',
        passContent.detailsTwo
      ),
      detailsThreeId: template.fields.details.three?.id,
      detailsThreeLabel: getLocalizedLabel(template.fields.details.three),
      detailsThreeValue: getLocalizedValue(
        template.fields.details.three,
        template.fields.details.three?.defaultValue || '',
        passContent.detailsThree
      ),
      detailsFourId: template.fields.details.four?.id,
      detailsFourLabel: getLocalizedLabel(template.fields.details.four),
      detailsFourValue: getLocalizedValue(
        template.fields.details.four,
        template.fields.details.four?.defaultValue || '',
        undefined
      ),
      detailsFiveId: template.fields.details.five?.id,
      detailsFiveLabel: getLocalizedLabel(template.fields.details.five),
      detailsFiveValue: getLocalizedValue(
        template.fields.details.five,
        template.fields.details.five?.defaultValue || '',
        undefined
      ),
      detailsSixId: template.fields.details.six?.id,
      detailsSixLabel: getLocalizedLabel(template.fields.details.six),
      detailsSixValue: getLocalizedValue(
        template.fields.details.six,
        template.fields.details.six?.defaultValue || '',
        undefined
      ),
      backOneId: template.fields.back.one?.id,
      backOneLabel: getLocalizedLabel(template.fields.back.one),
      backOneValue: prepareBackFieldValue(
        template.fields.back.one,
        getLocalizedValue(
          template.fields.back.one,
          template.fields.back.one?.defaultValue || '',
          undefined
        )
      ),
      backTwoId: template.fields.back.two?.id,
      backTwoLabel: getLocalizedLabel(template.fields.back.two),
      backTwoValue: prepareBackFieldValue(
        template.fields.back.two,
        getLocalizedValue(
          template.fields.back.two,
          template.fields.back.two?.defaultValue || '',
          undefined
        )
      ),
      backThreeId: template.fields.back.three?.id,
      backThreeLabel: getLocalizedLabel(template.fields.back.three),
      backThreeValue: prepareBackFieldValue(
        template.fields.back.three,
        getLocalizedValue(
          template.fields.back.three,
          template.fields.back.three?.defaultValue || '',
          undefined
        )
      ),
      backFourId: template.fields.back.four?.id,
      backFourLabel: getLocalizedLabel(template.fields.back.four),
      backFourValue: prepareBackFieldValue(
        template.fields.back.four,
        getLocalizedValue(
          template.fields.back.four,
          template.fields.back.four?.defaultValue || '',
          undefined
        )
      ),
      backFiveId: template.fields.back.five?.id,
      backFiveLabel: getLocalizedLabel(template.fields.back.five),
      backFiveValue: prepareBackFieldValue(
        template.fields.back.five,
        getLocalizedValue(
          template.fields.back.five,
          template.fields.back.five?.defaultValue || '',
          undefined
        )
      ),
      hideIdPlaceholder: true,
      passBackgroundColor: template.colors.passBackgroundColor,
      titleFontColor: template.colors.titleFontColor,
      bodyFontColor: template.colors.bodyFontColor,
      barcode:
        passContent?.barcode &&
        typeof passContent.barcode === 'object' &&
        passContent.barcode !== null &&
        'enabled' in passContent.barcode &&
        'type' in passContent.barcode &&
        'source' in passContent.barcode
          ? (passContent.barcode as QrFeature)
          : template.barcode,
      nfc: template.nfc,
      googleNfcEnabled: template.nfc.googleNfc,
      notificationHeader: template.notificationHeader,
      notificationMessage: appleLocationNotificationMessage,
      appleAppLink: template.appLinking.appleAppStoreUrl,
      googleAppLinkPlayStoreUrl: template.appLinking.googleAppPlayStoreUrl,
      googleAppLinkPackageName: template.appLinking.googleAppPackageName,
      googleAppLinkWebAppUrl: template.appLinking.googleWebUrl,
      googleAppLinkDisplayText: template.appLinking.googleDisplayText,
      passDesign:
        passContent.passDesign &&
        typeof passContent.passDesign === 'object' &&
        passContent.passDesign !== null &&
        'backgroundColor' in passContent.passDesign &&
        'logoImageUrl' in passContent.passDesign &&
        'bannerImageUrl' in passContent.passDesign &&
        'thumbnailImageUrl' in passContent.passDesign &&
        'watermarkColor' in passContent.passDesign &&
        'watermarkPosition' in passContent.passDesign &&
        'googleBannerWatermarkColor' in passContent.passDesign &&
        'googleBannerWatermarkPosition' in passContent.passDesign &&
        'appleBannerWatermarkColor' in passContent.passDesign &&
        'appleBannerWatermarkPosition' in passContent.passDesign
          ? (passContent.passDesign as PassDesign)
          : {
              backgroundColor: template.colors.passBackgroundColor || '',
              logoImageUrl: template.images.shared.logo || '',
              bannerImageUrl: getEffectiveTemplateImage(
                template.images,
                active === 'Google' ? 'google' : 'apple',
                'banner'
              ),
              thumbnailImageUrl: template.images.apple.thumbnail || '',
              watermarkColor: template.images.shared.watermarkColor || '',
              watermarkPosition: template.images.shared.watermarkPosition || '',
              googleBannerWatermarkColor:
                template.templateType === 'stampCard'
                  ? template.images.shared.watermarkColor || ''
                  : template.images.google.watermarkColor || '',
              googleBannerWatermarkPosition:
                template.templateType === 'stampCard'
                  ? template.images.shared.watermarkPosition || ''
                  : template.images.google.watermarkPosition || '',
              appleBannerWatermarkColor:
                template.templateType === 'stampCard'
                  ? template.images.shared.watermarkColor || ''
                  : template.images.apple.watermarkColor || '',
              appleBannerWatermarkPosition:
                template.templateType === 'stampCard'
                  ? template.images.shared.watermarkPosition || ''
                  : template.images.apple.watermarkPosition || '',
            },
      watermarkColor: template.images.shared.watermarkColor,
      watermarkPosition: template.images.shared.watermarkPosition,
      googleBannerWatermarkColor:
        template.templateType === 'stampCard'
          ? template.images.shared.watermarkColor
          : template.images.google.watermarkColor,
      googleBannerWatermarkPosition:
        template.templateType === 'stampCard'
          ? template.images.shared.watermarkPosition
          : template.images.google.watermarkPosition,
      appleBannerWatermarkColor:
        template.templateType === 'stampCard'
          ? template.images.shared.watermarkColor
          : template.images.apple.watermarkColor,
      appleBannerWatermarkPosition:
        template.templateType === 'stampCard'
          ? template.images.shared.watermarkPosition
          : template.images.apple.watermarkPosition,
      uuid: template.uuid,
      displayBannerWatermarkPreview: true,
      displayGoogleBannerWatermarkPreview: true,
      focusedField: focusedField,
      isFieldHighlighted: isFieldHighlighted,
      isStampBannerHighlighted: false,
    };

    if (!isStampCard || activeStampIndex > maxStampIndex) {
      return base;
    }

    if (isFrontOrBackFieldFocused) {
      return base;
    }

    const stampWord = STAMP_WORDS[activeStampIndex];
    const stampBannerField = getStampImageKey(
      stampWord
    ) as keyof typeof template.images.shared;
    const platformImages =
      active === 'Apple' ? template.images.apple : template.images.google;
    const stampBanner =
      (platformImages as Record<string, string>)[stampBannerField] ||
      (template.images.shared as Record<string, string>)[stampBannerField] ||
      base.bannerImage;

    const isStampBannerHighlighted =
      focusedField === `design.shared.${stampBannerField}` ||
      focusedField === `design.apple.${stampBannerField}` ||
      focusedField === `design.google.${stampBannerField}`;

    const overrides: Partial<typeof base> = {
      bannerImage: stampBanner,
      appleBannerImage: stampBanner,
      googleBannerImage: stampBanner,
      passDesign: {
        ...base.passDesign,
        bannerImageUrl: stampBanner,
      },
      isStampBannerHighlighted,
    };

    if (template.stampFeedbackTypes?.includes('sendPushNotification')) {
      const msg = template.notificationMessages[activeStampIndex];
      if (msg?.label && !/^locations\.\d+\.message$/.test(focusedField || '')) {
        overrides.notificationMessage = msg.label;
      }
    }

    // Map field id -> preview value key from template.fields so custom ids work
    const fieldIdToPreviewKey: Record<string, string> = {};
    const slotToPreviewKey: Record<string, string> = {
      header_one: 'headerOneValue',
      balance_one: 'headerOneValue',
      header_two: 'headerTwoValue',
      header_three: 'headerThreeValue',
      central_one: 'primaryOneValue',
      central_two: 'primaryTwoValue',
      details_one: 'detailsOneValue',
      details_two: 'detailsTwoValue',
      details_three: 'detailsThreeValue',
      details_four: 'detailsFourValue',
      details_five: 'detailsFiveValue',
      details_six: 'detailsSixValue',
      back_one: 'backOneValue',
      back_two: 'backTwoValue',
      back_three: 'backThreeValue',
      back_four: 'backFourValue',
      back_five: 'backFiveValue',
    };
    (
      [
        ['header', ['one', 'two', 'three']],
        ['central', ['one', 'two']],
        ['details', ['one', 'two', 'three', 'four', 'five', 'six']],
        ['back', ['one', 'two', 'three', 'four', 'five']],
      ] as const
    ).forEach(([section, slots]) => {
      slots.forEach((slot) => {
        const slotKey = `${section}_${slot}` as keyof typeof slotToPreviewKey;
        const previewKey = slotToPreviewKey[slotKey];
        const field = template.fields[section]?.[slot];
        if (field?.id && previewKey) {
          fieldIdToPreviewKey[field.id] = previewKey;
        }
      });
    });

    // Apply stamp short/long values for the active stamp stage (so each stage shows its values)
    const shortKey = getStampShortValueKey(
      stampWord
    ) as keyof typeof template.stampShortValues;
    const longKey = getStampLongValueKey(
      stampWord
    ) as keyof typeof template.stampLongValues;
    const shortVal = template.stampShortValues[shortKey];
    const longVal = template.stampLongValues[longKey];

    if (template.stampShortKey) {
      const key =
        fieldIdToPreviewKey[template.stampShortKey] ??
        slotToPreviewKey[
          template.stampShortKey as keyof typeof slotToPreviewKey
        ];
      if (key && key in base) {
        (overrides as Record<string, string>)[key] = shortVal ?? '';
      }
    }
    if (template.stampLongKey) {
      const key =
        fieldIdToPreviewKey[template.stampLongKey] ??
        slotToPreviewKey[
          template.stampLongKey as keyof typeof slotToPreviewKey
        ];
      if (key && key in base) {
        (overrides as Record<string, string>)[key] = longVal ?? '';
      }
    }

    const headerOneId = template.fields.header.one?.id;
    const isStampCountField =
      headerOneId === 'stamp_field' || headerOneId === 'stampLabel';
    if (isStampCountField) {
      overrides.headerOneValue = String(activeStampIndex);
    }

    return { ...base, ...overrides };
  }, [
    template.templateType,
    template.transitType,
    template.description,
    template.centralTitle,
    template.uuid,
    template.notificationHeader,
    template.notificationMessages,
    template.stampFeedbackTypes,
    template.stampShortValues,
    template.stampLongValues,
    template.stampShortKey,
    template.stampLongKey,
    template.stampCount,
    template.chosenGoogleLogo,
    template.locations,
    tFields,
    tImages,
    tColors,
    tAppLinking,
    tBarcode,
    tNfc,
    passContent,
    focusedField,
    previewLanguage,
    defaultLanguage,
    isStampCard,
    activeStampIndex,
    maxStampIndex,
    active,
    isFrontOrBackFieldFocused,
    selectedTab,
  ]);

  const resolvedPreviewData = externalPreviewData ?? previewData;

  const showClassicFace =
    (active === 'Apple' && !showAppleEnhancedFace) || active === 'iOS 26';
  const isBackSide = effectivePreviewSide === 'Back';
  const backFace = (
    <ApplePassPreview
      selectedPassTab="Back"
      previewData={previewData}
      featuresSide={featuresSide}
    />
  );

  const googleBgColor = useMemo(
    () =>
      resolvedPreviewData
        ? getGoogleBackgroundColor(resolvedPreviewData)
        : '#000000',
    [
      resolvedPreviewData?.passDesign?.backgroundColor,
      resolvedPreviewData?.passBackgroundColor,
    ]
  );

  // const [isValidImage, setIsValidImage] = useState(false);

  // const validateImageUrl = (url) => {
  //   const img = new Image();
  //   img.onload = () => {
  //     setIsValidImage(true);
  //   };
  //   img.onerror = () => {
  //     setIsValidImage(false);
  //   };
  //   img.src = url;
  // };

  // useEffect(() => {
  //   validateImageUrl(passTemplateCtx.googleAppLinkIconUrl);
  // }, [passTemplateCtx.googleAppLinkIconUrl]);

  const jsonTemplate = useMemo(() => {
    if (!isDevMode) return '{}';
    return JSON.stringify({ passTemplate: formatTemplatePayload(template) });
  }, [template, isDevMode]);

  const jsonPreviewData = useMemo(() => {
    if (!isPassPreview) return '{}';
    if (devModeData) return JSON.stringify(devModeData);
    if (resolvedPreviewData) return JSON.stringify(resolvedPreviewData);
    return '{}';
  }, [isPassPreview, devModeData, resolvedPreviewData]);

  useEffect(() => {
    if (selectedTab === 'Apple') {
      setActive(defaultTab);
    } else if (selectedTab === 'Google') {
      setActive('Google');
    }
  }, [selectedTab, defaultTab]);

  // Auto-switch preview platform based on focused field (Shared tab only)
  const autoSwitchedToGoogleRef = useRef(false);
  useEffect(() => {
    if (
      !focusedField ||
      isPassPreview ||
      selectedTab !== BUILDER_DEVICE_TAB.SHARED
    )
      return;
    const isLocationMessageFocused = /^locations\.\d+\.message$/.test(
      focusedField
    );
    if (focusedField === 'centralTitle') {
      autoSwitchedToGoogleRef.current = true;
      setActive('Google');
    } else if (isLocationMessageFocused) {
      autoSwitchedToGoogleRef.current = false;
      setActive('Apple');
    } else if (autoSwitchedToGoogleRef.current) {
      autoSwitchedToGoogleRef.current = false;
      setActive('Apple');
    }
  }, [focusedField, isPassPreview, selectedTab]);

  useEffect(() => {
    if (!isDevMode || !focusedField) return;
    const container = jsonContainerRef.current;
    if (!container) return;

    const id = setTimeout(() => {
      const firstSegment = focusedField.split('.').shift();
      const secondSegment = focusedField.split('.').slice(1, 2).join('.');
      const spans = Array.from(container.querySelectorAll('span'));
      let target: HTMLElement | undefined;

      // eslint-disable-next-line prefer-const
      target = spans.find((s) => {
        const t = (s.textContent || '').trim().replace(/"/g, '');
        return t === `${firstSegment}`;
      }) as HTMLElement | undefined;

      let secondTarget: HTMLElement | undefined;

      if (secondSegment) {
        const spansAfterTarget = spans.slice(spans.indexOf(target) + 1);
        const secondSegmentTarget = spansAfterTarget.find((s) => {
          const t = (s.textContent || '').trim().replace(/"/g, '');
          return t === `${secondSegment}`;
        }) as HTMLElement | undefined;
        secondTarget = secondSegmentTarget;
      }

      const scrollToElement = (element: HTMLElement) => {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const scrollTop =
          container.scrollTop +
          elementRect.top -
          containerRect.top -
          containerRect.height / 2 +
          elementRect.height / 2;
        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      };

      if (target) {
        scrollToElement(target);
      }
      if (secondTarget) {
        scrollToElement(secondTarget);
      }
    }, 50);

    return () => clearTimeout(id);
  }, [isDevMode, focusedField, jsonTemplate]);

  return (
    <Box
      border="2px solid lightGrey"
      borderRadius="10px"
      overflow="hidden"
      bg="white"
    >
      <Box
        p={8}
        pt={8}
        pb={0}
        bg="white"
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={0}
        position="relative"
      >
        {!isDevMode && (
          <ButtonGroup isAttached size="sm" m={0} zIndex="1">
            {buttons.map((label) => (
              <Button
                key={label}
                onClick={() => setActive(label)}
                variant={active === label ? 'gradient' : 'grey'}
                borderRadius="12px"
                w="110px"
                {...(active !== label && {
                  border: '1px solid',
                  borderColor: 'blackAlpha.100',
                })}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        )}
        {isDevMode ? (
          <>
            <Flex w="100%" justify="end" px={4}>
              <Tooltip
                label="Copy JSON"
                hasArrow
                placement="top"
                fontSize="12px"
                borderRadius="6px"
                p="10px"
                bg="secondaryDark"
                color="white"
              >
                <IconButton
                  alignSelf="end"
                  aria-label="copy"
                  variant="ghost"
                  _hover={{ bg: 'transparent', opacity: 0.8 }}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      isPassPreview ? jsonPreviewData : jsonTemplate
                    );
                    toast({
                      render: (props) => (
                        <CustomToast
                          status="success"
                          title="Copied to clipboard"
                          description=""
                          onClose={props.onClose}
                        />
                      ),
                    });
                  }}
                >
                  <CopyIcon color="orangePrimary" boxSize="20px" />
                </IconButton>
              </Tooltip>
            </Flex>
            <Box
              w="100%"
              maxW="500px"
              maxH="800px"
              overflow="scroll"
              ref={jsonContainerRef}
            >
              <JsonView
                value={JSON.parse(
                  isPassPreview ? jsonPreviewData : jsonTemplate
                )}
                displayObjectSize={false}
                displayDataTypes={false}
                enableClipboard={false}
              />
            </Box>
          </>
        ) : (
          <>
            <Flex
              align="center"
              justify="center"
              gap={stampNavOnRail ? 0 : isStampCard ? 6 : 2}
              w="100%"
              maxW={stampNavOnRail ? PREVIEW_DEVICE_WIDTH : '400px'}
              mb={isStampCard ? 0 : 2}
              mt={-2}
            >
              {!isPassPreview && isStampCard && !isFrontOrBackFieldFocused && (
                <IconButton
                  aria-label="Previous stamp"
                  icon={<CustomChevronLeftIcon boxSize={16} strokeWidth={1} />}
                  color="orangePrimary"
                  variant="ghost"
                  size="lg"
                  isDisabled={activeStampIndex <= 0}
                  onClick={() => setActiveStampIndex((i) => Math.max(0, i - 1))}
                  {...(stampNavOnRail
                    ? {
                        position: 'absolute' as const,
                        left: LEFT_OF_PREV_RAIL_CENTRE,
                        top: `calc(50% + ${stampNavOffset})`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                      }
                    : {})}
                  _disabled={{ color: '#AEAEAE', cursor: 'not-allowed' }}
                  _hover={{ bg: 'transparent', opacity: 0.7 }}
                />
              )}
              <Box
                w={PREVIEW_DEVICE_WIDTH}
                flexShrink={0}
                borderRadius="xl"
                overflow="hidden"
                pb={
                  active === 'Google' &&
                  showGoogleExpressivePass &&
                  !googleBarcodeView
                    ? 16
                    : active === 'Google' && googleBarcodeView
                      ? '22px'
                      : 0
                }
              >
                <AspectRatio ratio={9 / 20}>
                  <Box position="relative" overflow="hidden">
                    <Image
                      src={
                        active === 'Google'
                          ? showGoogleExpressivePass
                            ? GOOGLE_PIXEL9_PHONE_BASE64
                            : GOOGLE_PHONE_BASE64
                          : APPLE_PHONE_BASE64
                      }
                      alt="apple-screen"
                      objectFit="contain"
                      w="100%"
                      h="100%"
                      transform={
                        active === 'Google' && showGoogleExpressivePass
                          ? 'scale(1) translateX(0%) translateY(1.5%)'
                          : 'scale(1.05) translateX(-1%)'
                      }
                      transformOrigin="center"
                      pointerEvents="none"
                    />
                    {showClassicFace && (
                      <ApplePassPreview
                        selectedPassTab={effectivePreviewSide}
                        previewData={resolvedPreviewData}
                        featuresSide={featuresSide}
                      />
                    )}
                    {showAppleEnhancedFace &&
                      (isBackSide ? (
                        backFace
                      ) : previewEnhancedDesign === 'generic' ? (
                        <ApplePosterGenericPreview />
                      ) : (
                        <ApplePosterPreview />
                      ))}
                    {active === 'Enhanced' &&
                      (isBackSide ? backFace : <ApplePosterPreview />)}
                    {active === 'iOS 27+' &&
                      (isBackSide ? backFace : <ApplePosterGenericPreview />)}
                    {active === 'Google' &&
                      (googleBarcodeView && expressivePassRolloutEnabled ? (
                        <Box
                          position="absolute"
                          top="5.8%"
                          left="4%"
                          right="5%"
                          bottom="2.8%"
                          bg="white"
                          borderRadius="43px"
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          zIndex={1}
                          overflow="hidden"
                        >
                          <Box pt="24px" px="12px" w="full" alignSelf="stretch">
                            <GoogleArrowBackIcon
                              w="17px"
                              h="17px"
                              color="#000"
                            />
                          </Box>
                          <Flex
                            pt="32px"
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {(() => {
                              const logoImage =
                                resolvedPreviewData?.googleLogoImage ||
                                resolvedPreviewData?.iconLogo;
                              return logoImage ? (
                                <Box
                                  borderRadius="100%"
                                  boxSize="67px"
                                  border="1.5px solid rgba(0,0,0,0.09)"
                                  p="0"
                                >
                                  <Box
                                    borderRadius="100%"
                                    boxSize="100%"
                                    bg="white"
                                    overflow="hidden"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Image
                                      borderRadius="100%"
                                      boxSize="100%"
                                      objectFit="cover"
                                      src={logoImage}
                                      alt=""
                                    />
                                  </Box>
                                </Box>
                              ) : (
                                <Box h="65px" w="65px" />
                              );
                            })()}
                            <Text
                              fontSize="11px"
                              fontFamily="'Google Sans', sans-serif"
                              fontWeight="600"
                              lineHeight="130%"
                              textAlign="center"
                              color="#000"
                              mt="14px"
                            >
                              {resolvedPreviewData?.description ||
                                'Pass Description'}
                            </Text>
                            <Text
                              fontSize="24px"
                              fontWeight="800"
                              fontFamily="'Google Sans', sans-serif"
                              lineHeight="130%"
                              textAlign="center"
                              color="#000"
                              mt="6px"
                              px="15px"
                              wordBreak="break-word"
                            >
                              {template.passType === 'generic'
                                ? resolvedPreviewData?.primaryOneValue || ''
                                : resolvedPreviewData?.centralTitle ||
                                  resolvedPreviewData?.templateName ||
                                  'Google Pass Title'}
                            </Text>
                            {template.passType === 'generic' &&
                              resolvedPreviewData?.primaryOneLabel && (
                                <Text
                                  fontSize="11px"
                                  fontFamily="'Google Sans', sans-serif"
                                  fontWeight="600"
                                  lineHeight="130%"
                                  textAlign="center"
                                  color="#000"
                                  mt="4px"
                                >
                                  {resolvedPreviewData.primaryOneLabel}
                                </Text>
                              )}
                          </Flex>
                          <Flex
                            mt="20px"
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Flex
                              position="relative"
                              p="8px"
                              direction="column"
                              alignItems="center"
                            >
                              {(resolvedPreviewData?.isFieldHighlighted?.(
                                'barcode.enabled'
                              ) ||
                                resolvedPreviewData?.isFieldHighlighted?.(
                                  'barcode.type'
                                ) ||
                                resolvedPreviewData?.isFieldHighlighted?.(
                                  'barcode.source'
                                ) ||
                                resolvedPreviewData?.isFieldHighlighted?.(
                                  'barcode.value'
                                ) ||
                                resolvedPreviewData?.isFieldHighlighted?.(
                                  'barcode.displayText'
                                )) && (
                                <Box
                                  position="absolute"
                                  inset={0}
                                  border="2px solid #FF4935"
                                  borderRadius="10px"
                                  pointerEvents="none"
                                />
                              )}
                              <BarcodePreview
                                type={
                                  resolvedPreviewData?.barcode?.type ??
                                  template.barcode?.type
                                }
                                value={
                                  resolvedPreviewData?.barcode?.value ??
                                  template.barcode?.value
                                }
                              />
                              {((resolvedPreviewData?.barcode?.source ??
                                template.barcode?.source) !== 'custom' ||
                                ((resolvedPreviewData?.barcode?.source ??
                                  template.barcode?.source) === 'custom' &&
                                  (resolvedPreviewData?.barcode?.value ??
                                    template.barcode?.value))) && (
                                <Text
                                  fontSize="11px"
                                  fontWeight="600"
                                  fontFamily="'Google Sans', sans-serif"
                                  lineHeight="130%"
                                  textAlign="center"
                                  color="#000"
                                  mt="14px"
                                >
                                  {(resolvedPreviewData?.barcode?.value ??
                                    template.barcode?.value) !== ''
                                    ? (resolvedPreviewData?.barcode?.value ??
                                      template.barcode?.value)
                                    : (resolvedPreviewData?.barcode?.source ??
                                          template.barcode?.source) === 'custom'
                                      ? ''
                                      : (
                                          resolvedPreviewData?.barcode
                                            ?.source ?? template.barcode?.source
                                        )?.toUpperCase()}
                                </Text>
                              )}
                            </Flex>
                            <Text
                              fontSize="11px"
                              fontWeight="600"
                              fontFamily="'Google Sans', sans-serif"
                              lineHeight="130%"
                              textAlign="center"
                              color="#000"
                              mt="24px"
                              px="35px"
                            >
                              Scan the code or{' '}
                              <Box
                                as="span"
                                textDecoration="underline"
                                textUnderlineOffset="2px"
                                cursor="default"
                              >
                                learn how
                              </Box>{' '}
                              to tap your phone to the reader
                            </Text>
                          </Flex>
                        </Box>
                      ) : showGoogleExpressivePass ? (
                        <GoogleExpressivePassPreview
                          previewData={resolvedPreviewData}
                          createTemplate
                          notifOpen={
                            resolvedPreviewData?.focusedField ===
                              'notificationHeader' ||
                            /^notificationMessages\.\d+\.label$/.test(
                              resolvedPreviewData?.focusedField || ''
                            )
                          }
                          scrollContainerRef={googleScrollRef}
                          onScroll={handleGoogleScroll}
                        />
                      ) : (
                        <GooglePassPreview
                          selectedPassTab={effectiveGooglePreviewSide}
                          previewData={resolvedPreviewData}
                          featuresSide={featuresSide}
                        />
                      ))}
                    {active === 'Demo pass' && <ApplePosterDemo />}
                  </Box>
                </AspectRatio>
                {active === 'Google' &&
                  showGoogleExpressivePass &&
                  !googleBarcodeView && (
                    <GoogleScrollButton
                      ref={scrollButtonRef}
                      scrollContainerRef={googleScrollRef}
                      isAtBottom={googleIsAtBottom}
                      bounceCount={googleBounceCount}
                      isLightBg={getContrastYIQ(googleBgColor) === '#000000'}
                    />
                  )}
              </Box>
              {!isPassPreview && isStampCard && !isFrontOrBackFieldFocused && (
                <IconButton
                  aria-label="Next stamp"
                  icon={
                    <CustomChevronLeftIcon
                      boxSize={16}
                      strokeWidth={1}
                      transform="rotate(180deg)"
                    />
                  }
                  color="orangePrimary"
                  variant="ghost"
                  size="lg"
                  isDisabled={activeStampIndex >= maxStampIndex}
                  _disabled={{ color: '#AEAEAE', cursor: 'not-allowed' }}
                  onClick={() =>
                    setActiveStampIndex((i) => Math.min(maxStampIndex, i + 1))
                  }
                  {...(stampNavOnRail
                    ? {
                        position: 'absolute' as const,
                        left: RIGHT_OF_PREV_RAIL_CENTRE,
                        top: `calc(50% + ${stampNavOffset})`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                      }
                    : {})}
                  _hover={{ bg: 'transparent', opacity: 0.7 }}
                />
              )}
            </Flex>
            {!isPassPreview && isStampCard && !isFrontOrBackFieldFocused && (
              <Flex
                gap={2}
                justify="center"
                mb={4}
                data-testid="stamp-preview-stepper"
              >
                {Array.from({ length: maxStampIndex + 1 }, (_, i) => (
                  <Box
                    key={i}
                    as="button"
                    aria-label={i === 0 ? 'Unstamped' : `Stamp ${i}`}
                    aria-pressed={activeStampIndex === i}
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={activeStampIndex === i ? 'orangePrimary' : 'grey'}
                    opacity={activeStampIndex === i ? 1 : 0.5}
                    _hover={{ opacity: 1 }}
                    cursor="pointer"
                    onClick={() => setActiveStampIndex(i)}
                  />
                ))}
              </Flex>
            )}
            {active === 'Demo pass' && (
              <Flex
                align="center"
                gap={3}
                mt={-6}
                mb={4}
                w={PREVIEW_DEVICE_WIDTH + 20}
                px="14px"
                py="8px"
                bg="rgba(0, 155, 214, 0.1)"
                borderRadius="5px"
                alignSelf="center"
              >
                <CustomInfoIcon color="#009BD6" boxSize="21px" flexShrink={0} />
                <Text fontSize="14px" color="rgba(0, 0, 0, 0.8)" flex={1}>
                  Demo pass - tap to explore. Event details are set at pass
                  level.
                </Text>
              </Flex>
            )}
          </>
        )}
        <PreviewActionsPill
          isDevMode={isDevMode}
          setIsDevMode={setIsDevMode}
          handleFlipPreview={handleFlipPreview}
          hideFlip={active === 'Google' && showGoogleExpressivePass}
          showRevertButton={
            showAppleRevert ||
            (active === 'Google' &&
              expressivePassRolloutEnabled &&
              expressivePassRevertButtonEnabled &&
              !isTransit)
          }
          onRevertPreview={() => {
            const accountId = (meState?.account as { id?: string | number })
              ?.id;
            if (accountId) markStepSeen(String(accountId), revert.seenStep);
            revert.toggle();
          }}
          isRevertedPreview={revert.isReverted}
          revertLabel={revert.label}
          showBarcodeButton={
            active === 'Google' &&
            expressivePassRolloutEnabled &&
            !isTransit &&
            !!(
              resolvedPreviewData?.googleNfcEnabled ??
              (template.templateType === 'loyalty' ||
              template.templateType === 'stampCard'
                ? template.nfc?.googleNfc
                : template.nfc?.enabled)
            ) &&
            !!(
              resolvedPreviewData?.barcode?.enabled ?? template.barcode?.enabled
            )
          }
          onBarcodeView={() => {
            const accountId = (meState?.account as { id?: string | number })
              ?.id;
            if (accountId) markStepSeen(String(accountId), 'barcode');
            setGoogleBarcodeView((prev) => !prev);
          }}
          isBarcodeView={googleBarcodeView}
          isPassPreview={isPassPreview}
          addedLanguages={addedLanguages}
          passLanguages={passLanguages}
          passDefaultLanguage={passDefaultLanguage}
          selectedPassLanguage={selectedPassLanguage}
          onPassLanguageChange={onPassLanguageChange}
          revertButtonRef={revertButtonRef}
          barcodeButtonRef={barcodeButtonRef}
        />
        {active === 'Google' &&
          showGoogleExpressivePass &&
          !googleBarcodeView && (
            <GooglePreviewWizard
              scrollButtonRef={scrollButtonRef}
              barcodeButtonRef={barcodeButtonRef}
              revertButtonRef={revertButtonRef}
              scrolledToBottom={googleIsAtBottom}
              showBarcodeStep={
                !!(
                  expressivePassRolloutEnabled &&
                  !isTransit &&
                  (template.templateType === 'loyalty' ||
                  template.templateType === 'stampCard'
                    ? template.nfc?.googleNfc
                    : template.nfc?.enabled) &&
                  template.barcode?.enabled
                )
              }
              showRevertStep={
                !!(expressivePassRevertButtonEnabled && !isTransit)
              }
            />
          )}
        {showAppleEnhancedFace && (
          <GooglePreviewWizard
            scrollButtonRef={scrollButtonRef}
            barcodeButtonRef={barcodeButtonRef}
            revertButtonRef={revertButtonRef}
            showBarcodeStep={false}
            showRevertStep={false}
            showOlderDevicesStep
            showScrollStep={false}
          />
        )}
      </Box>
    </Box>
  );
};

export default PreviewPanel;
